import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Switch, FormControlLabel, Link, IconButton, Collapse,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import theme from '../theme';
import { useT } from '../i18n/langContext';
import { getConsent, setConsent, hasDecided } from '../lib/consent';
import { initAnalyticsIfConsented, disableAnalytics } from '../lib/analytics';

// Fired by the "Preferenze cookie" / "Cookie preferences" footer link to
// reopen the banner with the customize panel pre-expanded, from anywhere in
// the app, without needing a shared React context.
const REOPEN_EVENT = 'sg:cookie-preferences-open';

export function openCookiePreferences() {
  try {
    window.dispatchEvent(new Event(REOPEN_EVENT));
  } catch (e) {
    /* no-op in non-DOM environments */
  }
}

// Published cookie-policy page (src/CookiePolicy.jsx, route in App.jsx).
const COOKIE_POLICY_HREF = '/cookie-policy';

// First-visit cookie consent banner (Garante "Linee guida cookie", 10 June
// 2021): three equally prominent choices, no pre-ticked boxes, an X that
// behaves like "Rifiuta" ONLY on first visit — if the banner is reopened
// from the footer while a decision is already on record, X/Escape just
// closes it without touching that decision. "Personalizza" expands an
// inline panel with a disabled "Necessari" toggle and an off-by-default
// "Statistiche" toggle, alongside "Rifiuta" and "Salva preferenze". Mounted
// once at App level, outside the router, so it appears over every public
// page until a choice is on record (src/lib/consent.js — expires after 12
// months, then it re-asks).
export default function CookieBanner() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  // Whether a decision already existed at the moment this banner opened —
  // determines what X/Escape does (Reject vs. just-close).
  const [decidedOnOpen, setDecidedOnOpen] = useState(false);
  // Bumped on every open/reopen so the focus effect re-fires even when the
  // dialog was already visible (e.g. the footer link is clicked while the
  // first-visit banner is still showing) — "visible" alone wouldn't change.
  const [openToken, setOpenToken] = useState(0);
  const paperRef = useRef(null);
  // Whatever had focus right before the dialog opened, so it can be
  // restored when the dialog closes.
  const triggerRef = useRef(null);
  const bodyId = 'cookie-banner-body';

  function captureTriggerAndOpen() {
    try {
      triggerRef.current = document.activeElement;
    } catch (e) {
      triggerRef.current = null;
    }
    setOpenToken((n) => n + 1);
    setVisible(true);
  }

  function restoreFocusToTrigger() {
    const el = triggerRef.current;
    if (el && typeof el.focus === 'function' && document.contains(el)) {
      el.focus();
    }
    triggerRef.current = null;
  }

  useEffect(() => {
    if (!hasDecided()) {
      setDecidedOnOpen(false);
      captureTriggerAndOpen();
    }

    const onReopen = () => {
      const current = getConsent();
      setAnalyticsOn(current ? current.analytics : false);
      setDecidedOnOpen(current !== null);
      setCustomizing(true);
      captureTriggerAndOpen();
    };
    window.addEventListener(REOPEN_EVENT, onReopen);
    return () => window.removeEventListener(REOPEN_EVENT, onReopen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move focus into the dialog on every open/reopen (openToken changes even
  // if the dialog was already visible).
  useEffect(() => {
    if (visible && openToken > 0 && paperRef.current) {
      paperRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openToken]);

  // Escape closes the dialog (same semantics as the X button) — scoped to
  // the dialog itself via onKeyDown below, so Escape pressed while some
  // other MUI dialog/menu is open elsewhere on the page never reaches here
  // and never records a choice.
  function handleDialogKeyDown(e) {
    if (e.key === 'Escape') {
      handleCloseButton();
    }
  }

  function commit(analytics) {
    setConsent({ analytics });
    if (analytics) {
      initAnalyticsIfConsented();
    } else {
      disableAnalytics();
    }
    setVisible(false);
    setCustomizing(false);
    restoreFocusToTrigger();
  }

  function handleAcceptAll() {
    commit(true);
  }

  function handleReject() {
    commit(false);
  }

  // X / Escape: on first visit (no prior decision) this is Reject. If the
  // banner was reopened while a decision already existed, just close it —
  // closing to inspect/review preferences must never silently overwrite them.
  function handleCloseButton() {
    if (decidedOnOpen) {
      setVisible(false);
      setCustomizing(false);
      restoreFocusToTrigger();
    } else {
      handleReject();
    }
  }

  function handleOpenCustomize() {
    const current = getConsent();
    setAnalyticsOn(current ? current.analytics : false);
    setCustomizing(true);
  }

  function handleSaveCustom() {
    commit(analyticsOn);
  }

  if (!visible) return null;

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1400,
          display: 'flex', justifyContent: 'center', p: { xs: 1.5, md: 3 },
          pointerEvents: 'none',
        }}
      >
        <Paper
          ref={paperRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
          aria-describedby={bodyId}
          onKeyDown={handleDialogKeyDown}
          elevation={8}
          sx={{
            pointerEvents: 'auto',
            width: '100%', maxWidth: 760, position: 'relative',
            borderRadius: 3, p: { xs: 2.5, md: 3.5 },
            bgcolor: theme.home.paper, color: theme.home.ink,
            outline: 'none',
          }}
        >
          <IconButton
            aria-label={t(decidedOnOpen ? 'cookies.banner.closeAriaReopen' : 'cookies.banner.closeAriaFirstVisit')}
            onClick={handleCloseButton}
            size="small"
            sx={{ position: 'absolute', top: 10, right: 10 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Typography
            id="cookie-banner-title"
            sx={{ fontFamily: theme.home.display, fontWeight: 700, fontSize: '1.1rem', mb: 1, pr: 4 }}
          >
            {t('cookies.banner.title')}
          </Typography>

          <Typography id={bodyId} variant="body2" sx={{ color: theme.home.inkMuted, mb: 2.5 }}>
            {t('cookies.banner.body')}{' '}
            <Link component={RouterLink} to="/privacy" color="inherit" sx={{ textDecoration: 'underline' }}>
              {t('cookies.banner.privacyLink')}
            </Link>
            {' · '}
            <Link component={RouterLink} to={COOKIE_POLICY_HREF} color="inherit" sx={{ textDecoration: 'underline' }}>
              {t('cookies.banner.policyLink')}
            </Link>
          </Typography>

          <Collapse in={customizing}>
            <Box sx={{ borderTop: '1px solid rgba(20,24,29,0.1)', pt: 2, mb: 2 }}>
              <FormControlLabel
                control={<Switch checked disabled />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('cookies.banner.necessary')}</Typography>
                    <Typography variant="caption" sx={{ color: theme.home.inkMuted }}>{t('cookies.banner.necessaryHint')}</Typography>
                  </Box>
                }
                sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={analyticsOn}
                    onChange={(e) => setAnalyticsOn(e.target.checked)}
                    inputProps={{ 'aria-label': t('cookies.banner.analytics') }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('cookies.banner.analytics')}</Typography>
                    <Typography variant="caption" sx={{ color: theme.home.inkMuted }}>{t('cookies.banner.analyticsHint')}</Typography>
                  </Box>
                }
                sx={{ display: 'flex', alignItems: 'flex-start' }}
              />
            </Box>
          </Collapse>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="flex-end">
            {customizing ? (
              <>
                <Button variant="outlined" onClick={handleReject} sx={{ borderRadius: 999, borderColor: theme.home.ink, color: theme.home.ink }}>
                  {t('cookies.banner.reject')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveCustom}
                  sx={{ borderRadius: 999, bgcolor: theme.home.ink, '&:hover': { bgcolor: theme.home.inkHover } }}
                >
                  {t('cookies.banner.save')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" onClick={handleAcceptAll} sx={{ borderRadius: 999, borderColor: theme.home.ink, color: theme.home.ink }}>
                  {t('cookies.banner.acceptAll')}
                </Button>
                <Button variant="outlined" onClick={handleReject} sx={{ borderRadius: 999, borderColor: theme.home.ink, color: theme.home.ink }}>
                  {t('cookies.banner.reject')}
                </Button>
                <Button variant="outlined" onClick={handleOpenCustomize} sx={{ borderRadius: 999, borderColor: theme.home.ink, color: theme.home.ink }}>
                  {t('cookies.banner.customize')}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
